/**
 * AI Studio task registry.
 *
 * Declarative description of the AI jobs the Studio can run against one piece of
 * content. This module is PURE DATA (no React, no network) so it can be unit
 * tested and so the launcher UI, the progress dock, and the orchestrator hook all
 * read the same single source of truth.
 *
 * Each task maps 1:1 onto an existing hook/service pair that already ships and is
 * already used by the per-section AI panels — the Studio does not replace those
 * services, it only schedules them. See `useAiStudio` for the scheduling.
 *
 * ── Why `lane` matters ────────────────────────────────────────────────────────
 * Gemini free-tier keys allow roughly 20 requests/day/model and a low
 * requests/minute ceiling, so firing every job at once reliably 429s. Tasks are
 * therefore split into lanes:
 *   - lane "local"  → ffmpeg only, costs NO Gemini quota, safe to run immediately
 *                     and concurrently with anything else (scrubbing previews).
 *   - lane "gemini" → consumes model quota; the scheduler caps how many of these
 *                     run at once (see AI_STUDIO_GEMINI_CONCURRENCY).
 *
 * `geminiRequests` is the rough number of model calls the task spends, shown in
 * the launcher so an operator can see the cost of their selection before starting
 * rather than discovering it as a mid-run quota failure.
 */

/** Stable task ids. Also the keys used in the orchestrator's state map. */
export const AI_TASKS = {
  METADATA: "metadata",
  SUBTITLES: "subtitles",
  DUB: "dub",
  TRAILER: "trailer",
  THUMBNAILS: "thumbnails",
};

/** Execution lanes — see the module header for why these exist. */
export const AI_LANES = {
  LOCAL: "local",
  GEMINI: "gemini",
};

/**
 * How many quota-consuming (lane "gemini") tasks may run at once.
 * 2 is a deliberate compromise: fast enough that a 4-task selection doesn't
 * serialize into 15 minutes, conservative enough to stay under the free-tier
 * per-minute ceiling. Raise this once billing is enabled on the Gemini key.
 */
export const AI_STUDIO_GEMINI_CONCURRENCY = 2;

/**
 * Languages offered as translation / dub targets.
 * NOTE: the per-section subtitle panel in `Information/MediaAssests.js` keeps its
 * own copy of this list. Both must stay in sync; this one is the canonical list
 * for anything new. Codes match the backend's subtitleLanguageLabel map.
 */
export const AI_TARGET_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "hi", name: "Hindi" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "ar", name: "Arabic" },
  { code: "ta", name: "Tamil" },
  { code: "te", name: "Telugu" },
  { code: "bn", name: "Bengali" },
  { code: "ml", name: "Malayalam" },
  { code: "pt", name: "Portuguese" },
  { code: "zh", name: "Chinese" },
  { code: "ja", name: "Japanese" },
  { code: "ru", name: "Russian" },
];

/** Human name for a language code (falls back to the uppercased code). */
export const languageName = (code) => {
  const c = String(code || "").toLowerCase();
  return AI_TARGET_LANGUAGES.find((l) => l.code === c)?.name || c.toUpperCase();
};

/**
 * @typedef {Object} AiStudioTask
 * @property {string}   id
 * @property {string}   label            Short name shown on the card + dock row.
 * @property {string}   summary          One line: what the operator gets.
 * @property {string}   icon             Icon key resolved by the UI (see TASK_ICONS).
 * @property {string}   lane             AI_LANES.LOCAL | AI_LANES.GEMINI
 * @property {number}   geminiRequests   Rough model-call cost (0 for local tasks).
 * @property {number}   etaSeconds       Rough wall-clock estimate, used for ETA copy.
 * @property {boolean}  defaultOn        Pre-selected in the launcher.
 * @property {boolean}  needsLanguages   Requires at least one target language.
 * @property {string[]} fills            Plain-English list of what it populates.
 * @property {string}   [note]           Caveat surfaced on the card.
 */

/**
 * Ordered so the launcher reads naturally AND so the scheduler's default order
 * puts cheap/fast wins first — an operator sees results landing early instead of
 * waiting on the most expensive job.
 *
 * @type {AiStudioTask[]}
 */
export const AI_STUDIO_TASKS = [
  {
    id: AI_TASKS.THUMBNAILS,
    label: "Scrubbing previews",
    summary: "Hover-preview thumbnails for the player's seek bar.",
    icon: "thumbnails",
    lane: AI_LANES.LOCAL,
    geminiRequests: 0,
    etaSeconds: 90,
    defaultOn: true,
    needsLanguages: false,
    fills: ["Seek-bar preview sprite"],
    note: "Runs on ffmpeg only — costs no AI quota.",
  },
  {
    id: AI_TASKS.METADATA,
    label: "Metadata",
    summary: "Title, description, genres, rating, cast hints, poster & backdrop.",
    icon: "metadata",
    lane: AI_LANES.GEMINI,
    geminiRequests: 1,
    etaSeconds: 180,
    defaultOn: true,
    needsLanguages: false,
    fills: ["Basic information", "Technical specs", "Poster", "Backdrop"],
  },
  {
    id: AI_TASKS.SUBTITLES,
    label: "Subtitles",
    summary: "Transcribes the dialogue into timed .srt captions, plus translations.",
    icon: "subtitles",
    lane: AI_LANES.GEMINI,
    geminiRequests: 1,
    etaSeconds: 240,
    defaultOn: true,
    needsLanguages: false, // original language always produced; targets optional
    fills: ["Subtitle files"],
  },
  {
    id: AI_TASKS.TRAILER,
    label: "Trailer",
    summary: "Picks the strongest moments and cuts a short trailer.",
    icon: "trailer",
    lane: AI_LANES.GEMINI,
    geminiRequests: 1,
    etaSeconds: 300,
    defaultOn: false,
    needsLanguages: false,
    fills: ["Trailer video"],
  },
  {
    id: AI_TASKS.DUB,
    label: "Dubbed audio",
    summary: "Speaks the translated dialogue as a separate audio track per language.",
    icon: "dub",
    lane: AI_LANES.GEMINI,
    // ONE transcription request, like subtitles — and it's skipped entirely when
    // subtitles run in the same batch (see `sharesTranscriptWith`). The speech itself
    // is many calls against a SEPARATE text-to-speech quota, which is counted apart
    // because mixing the two pools into one number misleads on both.
    geminiRequests: 1,
    usesTts: true,
    etaSeconds: 600,
    defaultOn: false,
    needsLanguages: true,
    fills: ["Audio tracks"],
    note: "Slowest job — one text-to-speech call per line of dialogue.",
    // Running these together costs one transcription, not two: the subtitle job
    // caches a diarized transcript and the dub reuses it.
    sharesTranscriptWith: AI_TASKS.SUBTITLES,
    // Wait for subtitles when both are in the same run. Dubbing needs a transcript,
    // and the subtitle job has just produced one for the same audio — the server
    // caches it, so waiting turns two transcriptions into one. Without this the two
    // could overlap and the dub would pay for its own.
    //
    // Only a scheduling preference, never a hard requirement: if subtitles FAIL the
    // dub still runs and transcribes for itself.
    dependsOn: [AI_TASKS.SUBTITLES],
  },
];

/** Lookup by id. */
export const AI_STUDIO_TASK_MAP = AI_STUDIO_TASKS.reduce((acc, t) => {
  acc[t.id] = t;
  return acc;
}, {});

/** Trailer length choices offered in the launcher (seconds). */
export const TRAILER_LENGTH_OPTIONS = [
  { value: 20, label: "20s" },
  { value: 30, label: "30s" },
  { value: 45, label: "45s" },
  { value: 60, label: "60s" },
];

/**
 * Scrubbing-preview frame interval choices (seconds).
 * Lower = smoother scrubbing but a bigger sprite and a longer render.
 */
export const THUMBNAIL_INTERVAL_OPTIONS = [
  { value: 5, label: "Every 5s", hint: "Smoothest — best for short clips" },
  { value: 10, label: "Every 10s", hint: "Recommended" },
  { value: 20, label: "Every 20s", hint: "Lightest — best for long films" },
];

/** The default selection the launcher opens with. */
export const defaultSelection = () =>
  AI_STUDIO_TASKS.filter((t) => t.defaultOn).map((t) => t.id);

/**
 * Total estimated model requests for a selection — drives the launcher's
 * "spends about N AI requests" line, which is the number an operator uses to decide
 * whether a batch will exhaust the day's quota. So it has to reflect what the server
 * ACTUALLY spends, not the sum of the parts.
 *
 * Every job here costs one request regardless of how many languages it produces:
 * translations ride along in the same transcription call.
 *
 * The one adjustment: when a task shares its transcript with another task in the same
 * selection, only one of them pays for it. Dubbing alongside subtitles is therefore 1
 * request between them, not 2.
 *
 * Text-to-speech is deliberately NOT counted here — it draws on a separate quota with
 * its own limits, so folding it into this figure would misrepresent both. Use
 * `usesTts(taskIds)` to surface it alongside.
 */
export const estimateGeminiRequests = (taskIds = []) => {
  const selected = new Set(taskIds.filter((id) => AI_STUDIO_TASK_MAP[id]));
  let total = 0;
  selected.forEach((id) => {
    const task = AI_STUDIO_TASK_MAP[id];
    // Skip the request this task would have spent transcribing, when the task it
    // shares a transcript with is also running and will have produced it already.
    if (task.sharesTranscriptWith && selected.has(task.sharesTranscriptWith)) return;
    total += task.geminiRequests;
  });
  return total;
};

/** True when the selection includes a job that spends text-to-speech quota. */
export const usesTts = (taskIds = []) =>
  taskIds.some((id) => AI_STUDIO_TASK_MAP[id]?.usesTts);

/**
 * Wall-clock estimate for a selection, accounting for the lane concurrency —
 * local tasks overlap the Gemini ones for free, and Gemini tasks run
 * `AI_STUDIO_GEMINI_CONCURRENCY` at a time.
 *
 * Returns seconds. Deliberately rough: it exists to set expectations ("about
 * 6 minutes"), not to be a countdown.
 *
 * Task dependencies are intentionally NOT modelled here. The only one is dub-waits-
 * for-subtitles, and it roughly cancels out: the dub spends the subtitle job's
 * duration waiting, then skips a transcription of its own by reusing that transcript.
 */
export const estimateDurationSeconds = (
  taskIds = [],
  { dubLanguages = [], concurrency = AI_STUDIO_GEMINI_CONCURRENCY } = {}
) => {
  const picked = taskIds.map((id) => AI_STUDIO_TASK_MAP[id]).filter(Boolean);

  const localMax = picked
    .filter((t) => t.lane === AI_LANES.LOCAL)
    .reduce((max, t) => Math.max(max, t.etaSeconds), 0);

  const geminiCosts = picked
    .filter((t) => t.lane === AI_LANES.GEMINI)
    .map((t) =>
      t.id === AI_TASKS.DUB
        ? t.etaSeconds * Math.max(1, dubLanguages.length)
        : t.etaSeconds
    )
    .sort((a, b) => b - a); // longest first — matches how the scheduler drains

  // Greedy bin-packing across `concurrency` lanes: each task goes to the lane
  // that is currently free earliest. The finish time is the busiest lane.
  const lanes = new Array(Math.max(1, concurrency)).fill(0);
  geminiCosts.forEach((cost) => {
    let earliest = 0;
    for (let i = 1; i < lanes.length; i++) {
      if (lanes[i] < lanes[earliest]) earliest = i;
    }
    lanes[earliest] += cost;
  });

  return Math.max(localMax, ...lanes);
};

/** "about 4 minutes" / "under a minute" — friendly, never a false precision. */
export const formatEta = (seconds) => {
  if (!seconds || seconds <= 0) return "";
  if (seconds < 60) return "under a minute";
  const mins = Math.round(seconds / 60);
  return `about ${mins} minute${mins === 1 ? "" : "s"}`;
};

/** mm:ss for the dock's elapsed counter. */
export const formatElapsed = (ms) => {
  const total = Math.max(0, Math.floor((ms || 0) / 1000));
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, "0")}`;
};
